// Finance → Commissions → statement (CEO 2026-10-15). Every staff user sees
// their OWN commission statement for a date range (default: this month);
// Finance / admin can pick any rep. The API refuses anyone else's statement.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import {
  type CommissionLine,
  LINE_STATUS_LABEL,
  addDays,
  errReason,
  fmtDay,
  isFinanceRole,
  lineStatusTone,
  money,
  nameOr,
  ratePct,
  reviewText,
  roleLabel,
  usePlanStatus,
  usePlans,
} from "@/lib/compPlans";
import { Empty, Field, INPUT, LineStatusCell, Loading, Notice, Section, T_HEAD, T_NUM, T_ROW, T_TD, T_TH, T_WRAP } from "./parts";

interface Statement {
  repId: string;
  repName: string | null;
  plan: { name: string; version: number; rate: number | null } | null;
  from: string;
  to: string;
  openingEligible: string;
  newlyEligible: string;
  approvedBonuses: string;
  approvedAdjustments: string;
  paid: string;
  remaining: string;
  lines: CommissionLine[];
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Inclusive first/last day for a preset range. */
function range(kind: "this_month" | "last_month" | "this_year"): { from: string; through: string } {
  const now = new Date();
  if (kind === "this_year") return { from: `${now.getFullYear()}-01-01`, through: `${now.getFullYear()}-12-31` };
  const offset = kind === "last_month" ? -1 : 0;
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return { from: ymd(first), through: ymd(last) };
}

export function CommissionStatementPage() {
  const { user } = useAuth();
  const finance = isFinanceRole(user?.roles);
  const status = usePlanStatus();
  const ready = status.data?.ready === true;
  const plans = usePlans(ready && finance);

  const init = range("this_month");
  const [from, setFrom] = useState(init.from);
  const [through, setThrough] = useState(init.through);
  const [repId, setRepId] = useState("");

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(through) && through >= from;
  const to = valid ? addDays(through, 1) : ""; // the API's end is exclusive
  const q = useQuery({
    queryKey: ["commissions", "statement", repId, from, to],
    queryFn: () => {
      const p = new URLSearchParams({ from, to });
      if (repId) p.set("rep_id", repId);
      return api<Statement>(`/commissions/statement?${p.toString()}`);
    },
    enabled: ready && valid,
  });

  const header = (
    <PageHeader
      eyebrow="Finance / Commissions"
      title={finance ? "Rep statements" : "My commissions"}
      intro="What became eligible, what was approved and paid, and what's still owed — for the dates you choose."
      action={
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button variant="outline" className="h-11 text-[15px]" onClick={() => window.print()} disabled={!q.data}>
            <Printer className="h-4 w-4" aria-hidden /> Print
          </Button>
          {finance ? (
            <Link
              to="/finance/commissions"
              className="inline-flex h-11 items-center rounded-md border border-rule bg-white px-4 text-[15px] font-medium text-ink hover:bg-cream-100"
            >
              Back to Commissions
            </Link>
          ) : null}
        </div>
      }
    />
  );

  if (status.isLoading) return <div className="w-full">{header}<Loading /></div>;
  if (!ready) {
    return (
      <div className="w-full">
        {header}
        <Notice tone="info">Commission statements switch on with the new compensation plans — they're not live yet.</Notice>
      </div>
    );
  }

  const s = q.data;
  const preset = (k: "this_month" | "last_month" | "this_year") => {
    const r = range(k);
    setFrom(r.from);
    setThrough(r.through);
  };

  return (
    <div className="w-full">
      {header}

      {/* Print: only the statement itself — no app chrome, filters or buttons. */}
      <style>{PRINT_CSS}</style>
      <div className="mb-6 flex flex-wrap items-end gap-4 print:hidden">
        {finance && (
          <Field label="Rep">
            <select className={`${INPUT} w-72`} value={repId} onChange={(e) => setRepId(e.target.value)}>
              <option value="">Me</option>
              {(plans.data?.reps ?? [])
                .filter((r) => r.id !== user?.sub)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name}
                  </option>
                ))}
            </select>
          </Field>
        )}
        <Field label="From">
          <input type="date" className={`${INPUT} w-48`} value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Through">
          <input type="date" className={`${INPUT} w-48`} value={through} onChange={(e) => setThrough(e.target.value)} />
        </Field>
        <div className="flex flex-wrap gap-2 pb-0.5">
          <Button variant="outline" onClick={() => preset("this_month")}>
            This month
          </Button>
          <Button variant="outline" onClick={() => preset("last_month")}>
            Last month
          </Button>
          <Button variant="outline" onClick={() => preset("this_year")}>
            This year
          </Button>
        </div>
      </div>

      {!valid ? (
        <Notice tone="warn">Choose a start date on or before the end date.</Notice>
      ) : q.isError ? (
        <Notice tone="risk">{errReason(q.error)}</Notice>
      ) : !s ? (
        <Loading />
      ) : (
        <>
          <p className="mb-4 text-[16px] text-ink">
            <span className="font-semibold">{nameOr(s.repName)}</span>
            {s.plan ? (
              <span className="text-muted">
                {" "}
                · {s.plan.name} v{s.plan.version}
                {s.plan.rate != null ? ` · ${ratePct(s.plan.rate)} of gross profit` : ""}
              </span>
            ) : (
              <span className="text-muted"> · no plan assigned</span>
            )}
            <span className="text-muted">
              {" "}
              · {fmtDay(from)} – {fmtDay(through)}
            </span>
          </p>

          <div className="mb-8 overflow-hidden rounded-md border border-rule bg-white">
            <StatementLine label="Opening unpaid eligible balance" value={s.openingEligible} />
            <StatementLine label="Newly eligible" value={s.newlyEligible} sign="+" />
            <StatementLine label="Approved bonuses" value={s.approvedBonuses} sign="+" />
            <StatementLine label="Adjustments" value={s.approvedAdjustments} sign="+" />
            <StatementLine label="Paid" value={s.paid} sign="−" />
            <StatementLine label="Remaining eligible balance" value={s.remaining} total />
          </div>

          <Section
            title="Commission lines"
            intro="Orders that became eligible in these dates, plus every order still estimated (not yet paid in full, closed, and costs final)."
          >
            {s.lines.length === 0 ? (
              <Empty>No commission lines in these dates.</Empty>
            ) : (
              <div className={T_WRAP}>
                <table className="w-full min-w-[1100px]">
                  <thead className={T_HEAD}>
                    <tr>
                      <th className={T_TH}>Client / opportunity</th>
                      <th className={T_TH}>Order</th>
                      <th className={T_TH}>Your role</th>
                      <th className={`${T_TH} text-right`}>Gross profit</th>
                      <th className={T_TH}>Rate · share</th>
                      <th className={`${T_TH} text-right`}>Commission</th>
                      <th className={T_TH}>Status</th>
                      <th className={T_TH}>Eligible</th>
                      <th className={T_TH}>Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((r) => (
                      <tr key={r.id} className={T_ROW}>
                        <td className={T_TD}>
                          <span className="block font-medium">{r.client_name ?? "No client"}</span>
                          <Link to={`/projects/${r.project_id}`} className="text-[14px] text-muted hover:text-ink hover:underline">
                            {r.opportunity ?? "Opportunity"}
                          </Link>
                        </td>
                        <td className={`${T_TD} tabular-nums`}>#{r.order_number}</td>
                        <td className={T_TD}>{roleLabel(r.rep_role)}</td>
                        <td className={T_NUM}>{money(r.gross_profit)}</td>
                        <td className={T_TD}>
                          {ratePct(r.rate)} · {r.share_percent != null ? `${Number(r.share_percent)}%` : "—"}
                        </td>
                        <td className={`${T_NUM} font-semibold`}>{money(r.amount)}</td>
                        <td className={T_TD}>
                          <LineStatusCell
                            status={r.status}
                            label={LINE_STATUS_LABEL[r.status] ?? r.status}
                            tone={lineStatusTone(r.status)}
                            waitingOn={r.waiting_on}
                            reviewText={r.needs_review ? r.reviewText ?? reviewText(r.needs_review) : null}
                            reviewPill="Finance is reviewing"
                          />
                        </td>
                        <td className={`${T_TD} whitespace-nowrap`}>{fmtDay(r.eligible_at)}</td>
                        <td className={`${T_TD} whitespace-nowrap`}>{fmtDay(r.paid_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

const PRINT_CSS = `@media print {
  @page { margin: 14mm; }
  body { background: #fff !important; }
  aside, header.sticky, [data-feedback-widget] { display: none !important; }
  main { padding: 0 !important; }
  table { min-width: 0 !important; font-size: 11pt; }
  tr { break-inside: avoid; }
  a { text-decoration: none !important; color: inherit !important; }
}`;

function StatementLine({ label, value, sign, total }: { label: string; value: string; sign?: "+" | "−"; total?: boolean }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 border-b border-[#e7e7e4] px-5 py-3.5 last:border-0 ${
        total ? "bg-[#fafaf8]" : ""
      }`}
    >
      <span className={total ? "text-[17px] font-semibold text-ink" : "text-[16px] text-ink"}>
        {sign ? <span className="mr-2 inline-block w-3 text-muted">{sign}</span> : <span className="mr-2 inline-block w-3" />}
        {label}
      </span>
      <span className={`tabular-nums ${total ? "text-[22px] font-semibold" : "text-[18px]"} text-ink`}>{money(value)}</span>
    </div>
  );
}
